import React, { useState } from 'react';
import { HOLIDAY_BY_DATE } from '../data/holidays-cl';
import {
  getCustomDays,
  addCustomDay,
  removeCustomDay,
  type CustomNonWorkingDay,
  type DayPortion,
} from '../lib/nonWorkingDaysStore';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const DIAS_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function ymd(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

interface HolidayCalendarProps {
  // Se llama cuando el usuario agrega/quita un día no laboral, para que el
  // reporte se recalcule con la nueva meta.
  onChange?: () => void;
}

export function HolidayCalendar({ onChange }: HolidayCalendarProps) {
  const today = new Date();
  const [year, setYear] = useState(2026); // el demo vive en 2026
  const [month, setMonth] = useState(today.getFullYear() === 2026 ? today.getMonth() : 0);
  const [customDays, setCustomDays] = useState<CustomNonWorkingDay[]>(getCustomDays());
  // Modal para nombrar un nuevo día no laboral (reemplaza el window.prompt nativo).
  const [modalDate, setModalDate] = useState<string | null>(null);
  const [modalName, setModalName] = useState('');
  const [modalPortion, setModalPortion] = useState<DayPortion>('completo');

  const customByDate = new Map(customDays.map((d) => [d.date, d]));

  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // getDay: 0=dom..6=sáb → queremos lunes primero (0=lun..6=dom)
  const startOffset = (firstOfMonth.getDay() + 6) % 7;

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setMonth(0); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
  }

  function toggleCustomDay(date: string) {
    const isHoliday = !!HOLIDAY_BY_DATE[date];
    if (isHoliday) return; // los feriados legales no se editan
    if (customByDate.has(date)) {
      removeCustomDay(date);
      setCustomDays(getCustomDays());
      onChange?.();
    } else {
      // Abre el modal propio para nombrar el día.
      setModalName('Actividad oficina');
      setModalPortion('completo');
      setModalDate(date);
    }
  }

  function confirmModal() {
    if (!modalDate) return;
    addCustomDay(modalDate, modalName, modalPortion);
    setCustomDays(getCustomDays());
    setModalDate(null);
    onChange?.();
  }

  // Celdas del calendario (con huecos al inicio)
  const cells: (number | null)[] = [
    ...Array(startOffset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div className="bg-white rounded-xl shadow p-6">
      {/* Navegación */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
          aria-label="Mes anterior"
        >
          ←
        </button>
        <h3 className="text-lg font-bold text-slate-900">
          {MESES[month]} {year}
        </h3>
        <button
          onClick={nextMonth}
          className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
          aria-label="Mes siguiente"
        >
          →
        </button>
      </div>

      {/* Encabezado días de semana */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DIAS_SEMANA.map((d) => (
          <div key={d} className="text-center text-xs font-semibold text-slate-500 py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Grilla de días */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (day === null) return <div key={`empty-${i}`} />;

          const date = ymd(year, month, day);
          const holiday = HOLIDAY_BY_DATE[date];
          const custom = customByDate.get(date);
          const dow = new Date(year, month, day).getDay();
          const isWeekend = dow === 0 || dow === 6;

          const isMedia = custom?.portion === 'media_jornada';

          let cellClass = 'bg-white border-slate-200 hover:bg-slate-50';
          if (holiday) cellClass = 'bg-red-50 border-red-200';
          else if (isMedia) cellClass = 'bg-sky-50 border-sky-200';
          else if (custom) cellClass = 'bg-amber-50 border-amber-200';
          else if (isWeekend) cellClass = 'bg-slate-50 border-slate-100 text-slate-400';

          const numberColor = holiday
            ? 'text-red-700'
            : isMedia
            ? 'text-sky-700'
            : custom
            ? 'text-amber-700'
            : '';

          return (
            <button
              key={date}
              onClick={() => toggleCustomDay(date)}
              className={`min-h-[64px] rounded-lg border p-1.5 text-left transition flex flex-col ${cellClass} ${
                holiday ? 'cursor-default' : 'cursor-pointer'
              }`}
              title={holiday ? holiday.name : custom ? custom.name : 'Clic para marcar como no laboral'}
            >
              <span className={`text-sm font-semibold ${numberColor}`}>{day}</span>
              {holiday && (
                <span className="mt-auto inline-block text-[9px] leading-tight font-medium text-red-700 bg-red-100 rounded px-1 py-0.5 truncate">
                  {holiday.name}
                </span>
              )}
              {custom && (
                <span
                  className={`mt-auto inline-block text-[9px] leading-tight font-medium rounded px-1 py-0.5 truncate ${
                    isMedia ? 'text-sky-700 bg-sky-100' : 'text-amber-700 bg-amber-100'
                  }`}
                >
                  {isMedia ? '½ ' : ''}
                  {custom.name}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Leyenda */}
      <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-slate-600">
        <Legend color="bg-red-100 border-red-200" label="Feriado legal (no editable)" />
        <Legend color="bg-amber-100 border-amber-200" label="Actividad oficina (día completo)" />
        <Legend color="bg-sky-100 border-sky-200" label="Actividad oficina (media jornada)" />
        <Legend color="bg-slate-100 border-slate-200" label="Fin de semana" />
      </div>
      <p className="text-xs text-slate-400 mt-2">
        Haz clic en un día hábil para marcarlo como no laboral. Los feriados vienen pre-cargados.
      </p>

      {/* Modal propio para nombrar el día no laboral */}
      {modalDate && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setModalDate(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-fycom-navy text-white px-5 py-3">
              <h3 className="font-bold">Marcar día no laboral</h3>
              <p className="text-slate-300 text-xs">{formatLongDate(modalDate)}</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nombre (ej. actividad oficina, aniversario, etc.)
                </label>
                <input
                  autoFocus
                  value={modalName}
                  onChange={(e) => setModalName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmModal();
                    if (e.key === 'Escape') setModalDate(null);
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fycom-navy"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Jornada del día
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setModalPortion('completo')}
                    className={`px-3 py-2 rounded-lg text-sm border transition ${
                      modalPortion === 'completo'
                        ? 'bg-fycom-navy text-white border-fycom-navy'
                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    Día completo
                    <span className="block text-[10px] opacity-70">no se trabaja</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setModalPortion('media_jornada')}
                    className={`px-3 py-2 rounded-lg text-sm border transition ${
                      modalPortion === 'media_jornada'
                        ? 'bg-fycom-navy text-white border-fycom-navy'
                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    Media jornada
                    <span className="block text-[10px] opacity-70">se exige la mitad</span>
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setModalDate(null)}
                  className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmModal}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-fycom-navy text-white hover:bg-blue-900 transition"
                >
                  Marcar día
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DOW = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
function formatLongDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const dow = DOW[new Date(y, m - 1, d).getDay()];
  return `${dow} ${d} de ${MESES[m - 1]} ${y}`;
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-3 h-3 rounded border ${color}`} />
      {label}
    </span>
  );
}
